import React, { useState, useEffect } from 'react'
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material'
import { Search as SearchIcon } from '@mui/icons-material'
import { useDrugAutocompleteQuery } from '@/features/internal/drugs'

interface DrugSearchFieldProps {
  value?: string
  onSelect: (drugName: string) => void
  error?: boolean
  helperText?: string
  label?: string
  placeholder?: string
  required?: boolean
}

export const DrugSearchField: React.FC<DrugSearchFieldProps> = ({
  value = '',
  onSelect,
  error,
  helperText,
  label = '薬品名',
  placeholder = '薬品名を入力',
  required = true,
}) => {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState(value)

  // 外部からのvalue変更を反映
  useEffect(() => {
    setInputValue(value)
  }, [value])

  // 専用フックで医薬品検索（debounce内蔵）
  const { options, isLoading } = useDrugAutocompleteQuery(inputValue)

  const loading = isLoading

  return (
    <Autocomplete
      open={open}
      onOpen={() => {
        if (inputValue.length >= 2) {
          setOpen(true)
        }
      }}
      onClose={() => {
        setOpen(false)
      }}
      value={null}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'reset') {
          return
        }
        setInputValue(newInputValue)
        if (newInputValue.length >= 2) {
          setOpen(true)
        } else {
          setOpen(false)
        }
      }}
      onChange={(event, newValue) => {
        if (newValue) {
          const drugName = newValue.product_name
          setInputValue(drugName)
          onSelect(drugName)
          setOpen(false)
        }
      }}
      options={options}
      getOptionLabel={(option) => option.product_name}
      loading={loading}
      loadingText="検索中..."
      noOptionsText={
        inputValue.length < 2
          ? '2文字以上入力してください'
          : '該当する薬品が見つかりません'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : (
                  <SearchIcon color="action" />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box>
            <Typography variant="body2">{option.product_name}</Typography>
            {option.manufacturer && (
              <Typography variant="caption" color="text.secondary">
                {option.manufacturer}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      filterOptions={(x) => x}
      isOptionEqualToValue={(option, value) => option.id === value.id}
    />
  )
}
